import { useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Bell, CreditCard, Landmark, X, Loader2, CheckCircle2, Tag, Hand } from 'lucide-react';
import { api } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { useSse } from '@/hooks/useSse';
import { Picker } from '@/components/Picker';
import { BookingHeadSelect, useHeadLabel } from '@/components/BookingHeadSelect';
import { formatINR, formatTimeAgo, cn } from '@/lib/utils';

export type Bucket = 'TAGGED' | 'UNCLAIMED' | 'UPI';

// Current IST month as "YYYY-MM" — safe reference for the corporate-
// billing "no past months" rule. Adding the +330-min offset so the
// month rolls over at IST midnight regardless of server timezone.
function currentIstMonthKey(): string {
  const ms = Date.now() + 330 * 60_000;
  const d = new Date(ms);
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
}

// Options for the billing-month dropdown — current + next 11 months.
function billingMonthOptions(): { value: string; label: string }[] {
  const out: { value: string; label: string }[] = [];
  const now = new Date(Date.now() + 330 * 60_000);
  const y = now.getUTCFullYear();
  const m = now.getUTCMonth();
  const monthNames = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  for (let i = 0; i < 12; i++) {
    const yy = y + Math.floor((m + i) / 12);
    const mm = (m + i) % 12;
    out.push({
      value: `${yy}-${String(mm + 1).padStart(2, '0')}`,
      label: `${monthNames[mm]} ${yy}${i === 0 ? ' · this month' : ''}`,
    });
  }
  return out;
}

export interface DebitRow {
  id: string;
  paymentMethod: 'CARD' | 'BANK_TRANSFER';
  cardLastFour: string | null;
  cardNickname: string | null;
  bankNickname: string | null;
  vendor: string | null;
  beneficiaryName: string | null;
  amount: string | null;
  currency: string | null;
  amountFx: string | null;
  currencyFx: string | null;
  clientId: string | null;
  clientName: string | null;
  tripId: string | null;
  tripName: string | null;
  bookingHead: string | null;
  attributionType: string | null;
  ccPaymentCardId: string | null;
  ccPaymentCardLabel: string | null;
  createdAt: string;
  cardRequestId: string | null;
  bankTransferRequestId: string | null;
  initiatedByUserId: string | null;
  attributedByUserId: string | null;
  claimedByUserId: string | null;
  initiatorName: string | null;
  claimerName: string | null;
  smsVendor: string | null;
  hasOtpRequest: boolean;
  tagged: boolean;
  bucket: Bucket;
  /** Sub-field gaps within a Tagged row — trip / client / head missing.
   *  Surfaced as a warning chip, not as a separate bucket. */
  missingFields?: string[];
}

interface CountResp {
  count: number;
}

interface CardOption {
  id: string;
  nickname: string | null;
  bank: string | null;
  lastFour: string | null;
}
const cardLabel = (c: CardOption): string =>
  c.nickname ?? [c.bank, c.lastFour ? `••${c.lastFour}` : null].filter(Boolean).join(' ') ?? 'Card';

interface WalletOption {
  id: string;
  name: string;
}

/**
 * Header bell — recent card / bank debits in three tabs:
 *   Untagged  — you raised the OTP, the debit landed, not yet tagged
 *   Tagged    — same, fully tagged (client + trip + head, or office head)
 *   Unclaimed — debits with no OTP request raised in the app; claim → tag
 * Tagging is inline. Operators see their own; accounts/admin see all.
 */
export function RecentDebitsBell() {
  const { user } = useAuth();
  const role = user?.role;
  const visible = role === 'ADMIN' || role === 'ACCOUNTANT' || role === 'OPERATOR';
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<Bucket>('TAGGED');
  const wrapRef = useRef<HTMLDivElement>(null);

  const count = useQuery<CountResp>({
    queryKey: ['debits-unlogged-count'],
    queryFn: () => api('/api/transactions/unlogged-count'),
    refetchInterval: 60_000,
    enabled: visible,
  });

  const list = useQuery<DebitRow[]>({
    queryKey: ['debits-recent'],
    queryFn: () => api('/api/transactions/recent-debits'),
    enabled: visible && open,
    refetchInterval: open ? 30_000 : false,
  });

  useSse({
    onEvent: (ev) => {
      if (
        ev.type === 'transaction_unattributed' ||
        ev.type === 'transaction_confirmed' ||
        ev.type === 'debit_disambiguation' ||
        ev.type === 'attribution_reminder' ||
        ev.type === 'attribution_escalated'
      ) {
        qc.invalidateQueries({ queryKey: ['debits-unlogged-count'] });
        qc.invalidateQueries({ queryKey: ['debits-recent'] });
      }
    },
  });

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  if (!visible) return null;

  const badge = count.data?.count ?? 0;
  const items = list.data ?? [];
  const filtered = items.filter((i) => i.bucket === tab);
  const tabCount = (b: Bucket) => items.filter((i) => i.bucket === b).length;

  const onChanged = () => {
    qc.invalidateQueries({ queryKey: ['debits-recent'] });
    qc.invalidateQueries({ queryKey: ['debits-unlogged-count'] });
  };

  return (
    <div ref={wrapRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="relative inline-flex h-9 w-9 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-secondary/60 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        aria-label="Recent debits"
        aria-expanded={open}
      >
        <Bell className="h-4 w-4" />
        {badge > 0 ? (
          <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-destructive px-1 text-[9px] font-semibold text-destructive-foreground">
            {badge > 99 ? '99+' : badge}
          </span>
        ) : null}
      </button>

      {open ? (
        <div className="absolute left-0 top-12 z-50 w-[400px] max-w-[92vw] overflow-hidden rounded-xl border bg-card text-foreground shadow-2xl animate-content-in">
          <div className="flex items-center justify-between border-b border-zinc-200 px-4 py-3">
            <div>
              <h3 className="text-sm font-bold text-zinc-900">Recent debits</h3>
              <p className="text-[11px] text-zinc-500">Tag each debit to a client · trip · head</p>
            </div>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="rounded-md p-1 text-zinc-500 hover:bg-zinc-100 hover:text-zinc-900"
              aria-label="Close"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>

          <div className="flex gap-1 border-b border-zinc-200 px-4 py-2">
            <FilterChip active={tab === 'TAGGED'} onClick={() => setTab('TAGGED')}>
              Tagged
              {tabCount('TAGGED') > 0 ? <Pill active={tab === 'TAGGED'}>{tabCount('TAGGED')}</Pill> : null}
            </FilterChip>
            <FilterChip active={tab === 'UNCLAIMED'} onClick={() => setTab('UNCLAIMED')}>
              Unclaimed
              {tabCount('UNCLAIMED') > 0 ? <Pill active={tab === 'UNCLAIMED'}>{tabCount('UNCLAIMED')}</Pill> : null}
            </FilterChip>
            <FilterChip active={tab === 'UPI'} onClick={() => setTab('UPI')}>
              UPI
              {tabCount('UPI') > 0 ? <Pill active={tab === 'UPI'}>{tabCount('UPI')}</Pill> : null}
            </FilterChip>
          </div>

          <div className="max-h-[64vh] overflow-y-auto">
            {list.isLoading ? (
              <div className="px-4 py-10 text-center text-xs text-muted-foreground">
                <Loader2 className="mx-auto mb-2 h-4 w-4 animate-spin" />
                Loading…
              </div>
            ) : filtered.length === 0 ? (
              <p className="px-4 py-10 text-center text-xs text-muted-foreground">
                {tab === 'TAGGED'
                  ? 'No tagged debits yet.'
                  : tab === 'UPI'
                    ? 'No UPI debits.'
                    : 'Nothing unclaimed.'}
              </p>
            ) : (
              <ul className="divide-y">
                {filtered.map((d) => (
                  <DebitRowItem key={d.id} debit={d} meId={user?.id ?? ''} onChanged={onChanged} />
                ))}
              </ul>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function Pill({ active, children }: { active: boolean; children: React.ReactNode }) {
  return (
    <span
      className={cn(
        'ml-1 rounded-full px-1.5 text-[9px] font-bold',
        active ? 'bg-white/20 text-white' : 'bg-rose-100 text-rose-700',
      )}
    >
      {children}
    </span>
  );
}

function FilterChip({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-semibold transition-colors',
        active
          ? 'bg-zinc-900 text-white'
          : 'bg-zinc-100 text-zinc-600 hover:bg-zinc-200 hover:text-zinc-900',
      )}
    >
      {children}
    </button>
  );
}

export function DebitRowItem({
  debit,
  meId,
  onChanged,
}: {
  debit: DebitRow;
  meId: string;
  onChanged: () => void;
}) {
  const [tagging, setTagging] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const displayHead = useHeadLabel();

  const isCard = debit.paymentMethod === 'CARD';
  const Icon = isCard ? CreditCard : Landmark;
  const counterparty = debit.vendor ?? debit.beneficiaryName ?? null;

  let source: string;
  if (isCard) {
    const nick = debit.cardNickname;
    const last4 = debit.cardLastFour;
    if (nick && last4 && nick.includes(last4)) source = nick;
    else if (nick && last4) source = `${nick} ••${last4}`;
    else source = nick ?? (last4 ? `••${last4}` : 'Card');
  } else {
    source = debit.bankNickname ?? 'Bank transfer';
  }

  // Foreign-currency debits show as "USD 9" — via the FX side if it was
  // FX-matched, else straight off `currency` when it isn't INR.
  const amountDisplay =
    debit.amountFx && debit.currencyFx && debit.currencyFx !== 'INR'
      ? `${debit.currencyFx} ${debit.amountFx}`
      : debit.currency && debit.currency !== 'INR' && debit.amount
        ? `${debit.currency} ${debit.amount}`
        : debit.amount
          ? formatINR(debit.amount)
          : null;

  const claimedByMe = debit.claimedByUserId === meId;
  const claimedByOther = !!debit.claimedByUserId && !claimedByMe;
  // UPI debits behave like Unclaimed ones — claim to record which admin
  // did it, then tag — just surfaced in their own tab.
  const isClaimable = debit.bucket === 'UNCLAIMED' || debit.bucket === 'UPI';
  // A tagged row can still be re-tagged if sub-fields are missing (trip
  // / client / head), and a claimed unclaimed row becomes taggable once
  // the claim is set.
  const hasMissingFields = (debit.missingFields?.length ?? 0) > 0;
  const canTag = (debit.bucket === 'TAGGED' && hasMissingFields) || (isClaimable && claimedByMe);
  const canClaim = isClaimable && !debit.claimedByUserId && !debit.tagged;

  const claim = async () => {
    setBusy(true);
    setErr(null);
    try {
      await api(`/api/transactions/${debit.id}/claim`, { method: 'POST' });
      onChanged();
    } catch (e) {
      setErr((e as Error).message || 'Could not claim');
    } finally {
      setBusy(false);
    }
  };

  // Release a claim made by mistake — returns the debit to the shared pool.
  const unclaim = async () => {
    setBusy(true);
    setErr(null);
    try {
      await api(`/api/transactions/${debit.id}/unclaim`, { method: 'POST' });
      onChanged();
    } catch (e) {
      setErr((e as Error).message || 'Could not unclaim');
    } finally {
      setBusy(false);
    }
  };

  // Undo a mistaken tag. On a real debit SMS row → clears the tag +
  // claim so the row returns to Unclaimed. On a "Log a booking (no OTP)"
  // duplicate → server retires the row to IGNORED (audit-preserved).
  // Confirmation prompt because a wrong click here reverses ops work.
  const untag = async () => {
    if (!window.confirm('Undo this tag? The row will return to Unclaimed (or be retired if it was a manually-logged booking).')) {
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      await api(`/api/transactions/${debit.id}/untag`, { method: 'POST' });
      onChanged();
    } catch (e) {
      setErr((e as Error).message || 'Could not undo tag');
    } finally {
      setBusy(false);
    }
  };

  return (
    <li className="px-4 py-2.5">
      <div className="flex items-start gap-2.5">
        <span
          className={cn(
            'mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-md',
            debit.tagged ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-800',
          )}
        >
          <Icon className="h-3 w-3" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="text-sm font-bold tabular-nums text-zinc-900">
              {amountDisplay ?? <span className="text-zinc-400">no amount</span>}
            </span>
            <span className="ml-auto truncate text-[11px] text-zinc-500">
              {source} · {formatTimeAgo(debit.createdAt)}
            </span>
          </div>
          {(() => {
            const c = counterparty;
            const s = debit.smsVendor;
            const showBoth = c && s && c.toLowerCase() !== s.toLowerCase();
            return (
              <p className="mt-0.5 truncate text-xs text-zinc-700">
                {c ? (
                  <>
                    {c}
                    {showBoth ? (
                      <span className="text-zinc-400">
                        {' '}
                        · charged at <span className="font-medium text-zinc-600">{s}</span>
                      </span>
                    ) : null}
                  </>
                ) : s ? (
                  s
                ) : (
                  <span className="italic text-zinc-400">vendor pending</span>
                )}
              </p>
            );
          })()}

          {debit.tagged ? (
            <div className="mt-1.5 space-y-0.5">
              <div className={cn(
                'flex items-center gap-1 text-[11px]',
                hasMissingFields ? 'text-amber-700' : 'text-emerald-700',
              )}>
                {hasMissingFields ? (
                  <span className="inline-block h-3 w-3 shrink-0">⚠</span>
                ) : (
                  <CheckCircle2 className="h-3 w-3 shrink-0" />
                )}
                <span className="truncate">
                  {[
                    debit.clientName,
                    debit.tripName,
                    debit.bookingHead ? displayHead(debit.bookingHead) : null,
                    debit.ccPaymentCardLabel,
                  ]
                    .filter(Boolean)
                    .join(' · ') || 'no attribution set'}
                </span>
              </div>
              {hasMissingFields ? (
                <div className="text-[10px] text-amber-700">
                  needs {debit.missingFields!.join(' + ')} · tap "Fill" to complete
                </div>
              ) : null}
              {(debit.attributedByUserId === meId || debit.claimedByUserId === meId) && !tagging ? (
                <button
                  type="button"
                  onClick={untag}
                  disabled={busy}
                  className="mt-1 inline-flex items-center gap-1 rounded-md border border-zinc-200 bg-white px-2 py-0.5 text-[10px] font-medium text-rose-600 hover:bg-rose-50 disabled:opacity-50"
                >
                  {busy ? 'Undoing…' : 'Undo tag'}
                </button>
              ) : null}
            </div>
          ) : null}

          {!debit.tagged && claimedByOther ? (
            <p className="mt-1.5 text-[11px] text-zinc-500">
              Claimed by {debit.claimerName ?? 'a teammate'} — awaiting tag
            </p>
          ) : null}

          {!debit.tagged && !tagging ? (
            <div className="mt-1.5 flex items-center gap-2">
              {canClaim ? (
                <button
                  type="button"
                  onClick={claim}
                  disabled={busy}
                  className="inline-flex items-center gap-1 rounded-md border border-zinc-300 bg-white px-2 py-0.5 text-[11px] font-semibold text-zinc-700 hover:bg-zinc-50 disabled:opacity-50"
                >
                  <Hand className="h-3 w-3" />
                  {busy ? 'Claiming…' : 'Claim'}
                </button>
              ) : null}
              {canTag ? (
                <button
                  type="button"
                  onClick={() => setTagging(true)}
                  className="inline-flex items-center gap-1 rounded-md bg-blue-600 px-2 py-0.5 text-[11px] font-semibold text-white hover:bg-blue-700"
                >
                  <Tag className="h-3 w-3" />
                  {hasMissingFields ? 'Fill' : 'Tag now'}
                </button>
              ) : null}
              {claimedByMe ? (
                <button
                  type="button"
                  onClick={unclaim}
                  disabled={busy}
                  className="inline-flex items-center gap-1 rounded-md border border-zinc-300 bg-white px-2 py-0.5 text-[11px] font-semibold text-rose-600 hover:bg-rose-50 disabled:opacity-50"
                >
                  {busy ? 'Releasing…' : 'Unclaim'}
                </button>
              ) : null}
              {!canClaim && !canTag && isClaimable && !claimedByOther ? (
                <span className="text-[11px] italic text-zinc-400">untagged</span>
              ) : null}
            </div>
          ) : null}
          {err ? <p className="mt-1 text-[11px] text-rose-700">{err}</p> : null}

          {tagging ? (
            <TagForm
              debitId={debit.id}
              defaultVendor={debit.vendor ?? debit.smsVendor ?? ''}
              onCancel={() => setTagging(false)}
              onSaved={() => {
                setTagging(false);
                onChanged();
              }}
            />
          ) : null}
        </div>
      </div>
    </li>
  );
}

function TagForm({
  debitId,
  defaultVendor,
  onCancel,
  onSaved,
}: {
  debitId: string;
  defaultVendor: string;
  onCancel: () => void;
  onSaved: () => void;
}) {
  const [mode, setMode] = useState<'CLIENT' | 'OFFICE_EXPENSE' | 'WALLET_LOAD'>('CLIENT');
  const [walletId, setWalletId] = useState('');
  const [client, setClient] = useState<{ id: string; label: string } | null>(null);
  const [clientIsCorporate, setClientIsCorporate] = useState(false);
  const [trip, setTrip] = useState<{ id: string; label: string } | null>(null);
  const [travelMonth, setTravelMonth] = useState<string>(currentIstMonthKey());
  const [head, setHead] = useState('');
  // Vendor is now a Picker (search existing + inline add) instead of
  // free text — keeps the vendor directory clean and lets reports group
  // by vendor_id. Seed the picker with the SMS-parsed name so a single
  // tap saves for known merchants.
  const [vendorPick, setVendorPick] = useState<{ id: string; label: string } | null>(
    defaultVendor ? { id: '', label: defaultVendor } : null,
  );
  const [vendor, setVendor] = useState(defaultVendor);
  const [ccCardId, setCcCardId] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Reflect the picked client's type into the corporate toggle. Idempotent —
  // if ops toggles off after picking a real corporate row, we mark that row
  // LEISURE on save (server accepts the flip).
  useEffect(() => {
    let cancelled = false;
    if (!client?.id) { setClientIsCorporate(false); return; }
    (async () => {
      try {
        const c = await api<{ clientType?: string }>(`/api/clients/${client.id}`);
        if (!cancelled) setClientIsCorporate(c.clientType === 'CORPORATE');
      } catch { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, [client?.id]);

  // Cards (for CC_PAYMENT) + wallets (for WALLET_LOAD) — fetched only
  // when the form is open.
  const cards = useQuery<CardOption[]>({
    queryKey: ['debit-tag-cards'],
    queryFn: () => api('/api/cards'),
  });
  const wallets = useQuery<WalletOption[]>({
    queryKey: ['debit-tag-wallets'],
    queryFn: () => api('/api/wallets'),
  });

  const searchVendors = async (q: string) => {
    const params = new URLSearchParams({ limit: '30' });
    if (q) params.set('q', q);
    const r = await api<Array<{ id: string; name: string; shortName: string | null; vendorKind: string }>>(
      `/api/vendors?${params}`,
    );
    return r.map((v) => ({
      id: v.id,
      label: v.shortName ? `${v.shortName} · ${v.name}` : v.name,
      sub: v.vendorKind === 'INDIVIDUAL_PAYOUT_PLATFORM' ? 'payout platform' : undefined,
    }));
  };
  const createVendor = async (name: string) => {
    const v = await api<{ id: string; name: string; shortName: string | null }>('/api/vendors', {
      method: 'POST',
      body: { name, category: 'OTHER', vendorKind: 'MERCHANT' },
    });
    return { id: v.id, label: v.shortName ? `${v.shortName} · ${v.name}` : v.name };
  };

  const searchClients = async (q: string) => {
    const params = new URLSearchParams({ limit: '20' });
    if (q) params.set('q', q);
    const r = await api<{ id: string; name: string; status: string }[]>(`/api/clients?${params}`);
    return r.map((c) => ({ id: c.id, label: c.name, sub: c.status.toLowerCase() }));
  };
  const createClient = async (name: string) => {
    const c = await api<{ id: string; name: string }>('/api/clients', {
      method: 'POST',
      body: { name, status: 'LEAD' },
    });
    return { id: c.id, label: c.name };
  };
  const searchTrips = async (q: string) => {
    if (!client) return [];
    const params = new URLSearchParams({ clientId: client.id, limit: '20' });
    if (q) params.set('q', q);
    const r = await api<{ id: string; name: string; destination: string | null }[]>(
      `/api/trips?${params}`,
    );
    return r.map((t) => ({ id: t.id, label: t.name, sub: t.destination ?? undefined }));
  };
  const createTrip = async (name: string) => {
    if (!client) return null;
    const t = await api<{ id: string; name: string }>('/api/trips', {
      method: 'POST',
      body: { clientId: client.id, name, status: 'PLANNING' },
    });
    return { id: t.id, label: t.name };
  };

  const save = async () => {
    if (mode === 'CLIENT') {
      if (!client) { setErr('Pick a client.'); return; }
      if (clientIsCorporate) {
        if (!travelMonth) { setErr('Pick a billing month.'); return; }
        if (travelMonth < currentIstMonthKey()) {
          setErr('Billing month cannot be in the past.'); return;
        }
      } else if (!trip) { setErr('Pick a trip (or mark the client as corporate).'); return; }
    }
    if (mode === 'WALLET_LOAD') {
      if (!walletId) { setErr('Pick which wallet was loaded.'); return; }
    }
    // Wallet-load short-circuits the head picker — head is always WALLET_LOAD.
    const effectiveHead = mode === 'WALLET_LOAD' ? 'WALLET_LOAD' : head;
    if (!effectiveHead) {
      setErr('Pick a booking head.');
      return;
    }
    if (effectiveHead === 'CC_PAYMENT' && !ccCardId) {
      setErr('Pick the credit card this payment cleared.');
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      // Wallet-load submits as an office-expense flavour (server-side
      // shape) with WALLET_LOAD head and the wallet's name stamped as
      // vendor. Vendor picker is skipped — the wallet IS the vendor.
      const pickedWallet = (wallets.data ?? []).find((w) => w.id === walletId);
      const submitMode = mode === 'WALLET_LOAD' ? 'OFFICE_EXPENSE' : mode;
      const submitVendor =
        mode === 'WALLET_LOAD'
          ? (pickedWallet?.name ?? 'Wallet')
          : (vendor.trim() || undefined);
      await api(`/api/transactions/${debitId}/tag`, {
        method: 'POST',
        body: {
          mode: submitMode,
          clientId: mode === 'CLIENT' ? client!.id : undefined,
          tripId: mode === 'CLIENT' && !clientIsCorporate ? trip!.id : undefined,
          travelMonth: mode === 'CLIENT' && clientIsCorporate ? travelMonth : undefined,
          markClientCorporate: mode === 'CLIENT' && clientIsCorporate ? true : undefined,
          bookingHead: effectiveHead,
          vendorId: mode === 'WALLET_LOAD' ? undefined : (vendorPick && vendorPick.id ? vendorPick.id : undefined),
          vendor: submitVendor,
          ccPaymentCardId: effectiveHead === 'CC_PAYMENT' ? ccCardId : undefined,
        },
      });
      onSaved();
    } catch (e) {
      setErr((e as Error).message || 'Could not save tag');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-2 space-y-2 rounded-lg border border-blue-200 bg-blue-50/50 p-2.5">
      <div className="flex gap-1">
        {(
          [
            ['CLIENT', 'Client booking'],
            ['WALLET_LOAD', 'Wallet load'],
            ['OFFICE_EXPENSE', 'Office expense'],
          ] as const
        ).map(([m, lbl]) => (
          <button
            key={m}
            type="button"
            onClick={() => {
              setMode(m);
              setHead('');
            }}
            className={cn(
              'flex-1 rounded-md px-2 py-1 text-[11px] font-semibold transition-colors',
              mode === m
                ? 'bg-zinc-900 text-white'
                : 'bg-white text-zinc-600 ring-1 ring-zinc-200 hover:bg-zinc-50',
            )}
          >
            {lbl}
          </button>
        ))}
      </div>
      <div>
        {mode === 'WALLET_LOAD' ? (
          <>
            <label className="text-[10px] font-semibold uppercase tracking-wide text-zinc-500">
              Wallet loaded
            </label>
            <select
              value={walletId}
              onChange={(e) => setWalletId(e.target.value)}
              className="mt-0.5 h-8 w-full rounded-md border border-input bg-background px-2 text-xs"
            >
              <option value="">— Select wallet —</option>
              {(wallets.data ?? []).map((w) => (
                <option key={w.id} value={w.id}>{w.name}</option>
              ))}
            </select>
            <p className="mt-0.5 text-[10px] text-zinc-500">
              Booking head auto-set to Wallet load.
            </p>
          </>
        ) : (
          <>
            <label className="text-[10px] font-semibold uppercase tracking-wide text-zinc-500">Vendor</label>
            <Picker
              value={vendorPick && vendorPick.id ? vendorPick : null}
              onChange={(v) => {
                setVendorPick(v);
                setVendor(v?.label ?? '');
              }}
              search={searchVendors}
              onCreateNew={createVendor}
              placeholder={defaultVendor ? `${defaultVendor} — or search` : 'Search or add a vendor'}
            />
          </>
        )}
      </div>
      {mode === 'CLIENT' ? (
        <>
          <div>
            <label className="text-[10px] font-semibold uppercase tracking-wide text-zinc-500">
              Client
            </label>
            <Picker
              value={client}
              onChange={(v) => {
                setClient(v);
                setTrip(null);
              }}
              search={searchClients}
              onCreateNew={createClient}
              placeholder="Search or add a client"
            />
          </div>
          {client ? (
            <label className="flex items-center gap-1.5 text-[11px] font-medium text-zinc-700 cursor-pointer">
              <input
                type="checkbox"
                checked={clientIsCorporate}
                onChange={(e) => setClientIsCorporate(e.target.checked)}
                className="h-3.5 w-3.5"
              />
              Corporate client (monthly billing, no trip)
            </label>
          ) : null}
          {client && clientIsCorporate ? (
            <div>
              <label className="text-[10px] font-semibold uppercase tracking-wide text-zinc-500">
                Billing month
              </label>
              <select
                value={travelMonth}
                onChange={(e) => setTravelMonth(e.target.value)}
                className="mt-0.5 h-8 w-full rounded-md border border-input bg-background px-2 text-xs"
              >
                {billingMonthOptions().map((m) => (
                  <option key={m.value} value={m.value}>{m.label}</option>
                ))}
              </select>
              <p className="mt-0.5 text-[10px] text-zinc-500">
                Defaults to this month. Can be a future month; never past.
              </p>
            </div>
          ) : null}
          {client && !clientIsCorporate ? (
            <div>
              <label className="text-[10px] font-semibold uppercase tracking-wide text-zinc-500">
                Trip
              </label>
              <Picker
                value={trip}
                onChange={setTrip}
                search={searchTrips}
                onCreateNew={createTrip}
                placeholder="Search or add a trip"
                refetchKey={client.id}
              />
            </div>
          ) : null}
        </>
      ) : null}
      {mode === 'WALLET_LOAD' ? null : (
        <div>
          <label className="text-[10px] font-semibold uppercase tracking-wide text-zinc-500">
            Booking head
          </label>
          <BookingHeadSelect
            value={head}
            onChange={setHead}
            category={mode === 'CLIENT' ? 'CLIENT_BOOKING' : 'OPERATING_EXPENSE'}
            className="mt-0.5 h-8 w-full rounded-md border border-input bg-background px-2 text-xs"
          />
        </div>
      )}
      {head === 'CC_PAYMENT' ? (
        <div>
          <label className="text-[10px] font-semibold uppercase tracking-wide text-zinc-500">
            Credit card paid
          </label>
          <select
            value={ccCardId}
            onChange={(e) => setCcCardId(e.target.value)}
            className="mt-0.5 h-8 w-full rounded-md border border-input bg-background px-2 text-xs"
          >
            <option value="">— Select card —</option>
            {(cards.data ?? []).map((c) => (
              <option key={c.id} value={c.id}>
                {cardLabel(c)}
              </option>
            ))}
          </select>
        </div>
      ) : null}
      {head === 'WALLET_LOAD' ? (
        <div>
          <label className="text-[10px] font-semibold uppercase tracking-wide text-zinc-500">
            Wallet loaded
          </label>
          <select
            value=""
            onChange={(e) => {
              const w = (wallets.data ?? []).find((x) => x.id === e.target.value);
              if (w) setVendor(w.name);
            }}
            className="mt-0.5 h-8 w-full rounded-md border border-input bg-background px-2 text-xs"
          >
            <option value="">— Select wallet (sets vendor) —</option>
            {(wallets.data ?? []).map((w) => (
              <option key={w.id} value={w.id}>
                {w.name}
              </option>
            ))}
          </select>
        </div>
      ) : null}
      {err ? <p className="text-[11px] text-rose-700">{err}</p> : null}
      <div className="flex justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          className="rounded-md px-2.5 py-1 text-[11px] font-semibold text-zinc-600 hover:bg-zinc-100"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={save}
          disabled={busy}
          className="rounded-md bg-blue-600 px-3 py-1 text-[11px] font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
        >
          {busy ? 'Saving…' : 'Save tag'}
        </button>
      </div>
    </div>
  );
}
